import React, { useState, useEffect } from 'react';
import { useCookieConsent } from '../hooks/useCookieConsent';
import CookieSettings from '../components/CookieSettings';

const CookieTest = () => {
  const {
    hasConsent,
    preferences,
    isLoading,
    hasConsentFor,
    getConsentDate,
    isConsentExpired,
    trackEvent,
    trackPage,
    COOKIE_TYPES
  } = useCookieConsent();

  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [testData, setTestData] = useState({
    pageViews: 0,
    buttonClicks: 0,
    formSubmissions: 0
  });

  useEffect(() => {
    // Track page view when component mounts
    trackPage('/cookie-test');
    setTestData(prev => ({ ...prev, pageViews: prev.pageViews + 1 }));
  }, [trackPage]);

  const handleTestButtonClick = () => {
    trackEvent('test_button_click', {
      event_category: 'engagement',
      event_label: 'Cookie Test Page',
      value: 1
    });
    setTestData(prev => ({ ...prev, buttonClicks: prev.buttonClicks + 1 }));
  };

  const handleTestFormSubmit = (e) => {
    e.preventDefault();
    trackEvent('test_form_submit', {
      event_category: 'form',
      event_label: 'Cookie Test Form',
      value: 1
    });
    setTestData(prev => ({ ...prev, formSubmissions: prev.formSubmissions + 1 }));
    alert('Form submitted! Check your analytics for the event.');
  };

  const handleTrackCustomEvent = () => {
    trackEvent('custom_event_test', {
      event_category: 'testing',
      event_label: 'Custom Event',
      custom_parameter: 'test_value',
      value: 42
    });
  };

  const handleStoreTestCookie = () => {
    if (hasConsentFor(COOKIE_TYPES.FUNCTIONAL)) {
      document.cookie = `terrapin_test_cookie=test_value_${Date.now()}; expires=${new Date(Date.now() + 86400000).toUTCString()}; path=/; SameSite=Lax`;
      alert('Test cookie stored! (Only if functional cookies are enabled)');
    } else {
      alert('Functional cookies not enabled - cannot store test cookie');
    }
  };

  const getStoredTestCookie = () => {
    const cookies = document.cookie.split(';');
    const testCookie = cookies.find(cookie => cookie.trim().startsWith('terrapin_test_cookie='));
    return testCookie ? testCookie.split('=')[1] : null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading cookie consent status...</p>
        </div>
      </div>
    );
  }

  const consentDate = getConsentDate();
  const testCookieValue = getStoredTestCookie();

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Cookie Consent Test Page</h1>
          
          <div className="mb-8">
            <p className="text-gray-700 mb-4">
              This page demonstrates the cookie consent functionality and allows you to test 
              cookie storage, analytics tracking, and consent management.
            </p>
            
                         <button
               onClick={() => setShowCookieSettings(true)}
               className="btn-base btn-primary"
             >
               Open Cookie Settings
             </button>
          </div>

          {/* Consent Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Consent Status</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-700">Has Consent:</span>
                  <span className={`font-medium ${hasConsent ? 'text-green-600' : 'text-red-600'}`}>
                    {hasConsent ? 'Yes' : 'No'}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-700">Consent Date:</span>
                  <span className="text-gray-600">
                    {consentDate ? consentDate.toLocaleDateString() : 'Not set'}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-700">Consent Expired:</span>
                  <span className={`font-medium ${isConsentExpired() ? 'text-red-600' : 'text-green-600'}`}>
                    {isConsentExpired() ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookie Preferences</h2>
              
              <div className="space-y-3">
                {Object.entries(COOKIE_TYPES).map(([key, type]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-700 capitalize">{type}:</span>
                    <span className={`font-medium ${hasConsentFor(type) ? 'text-green-600' : 'text-red-600'}`}>
                      {hasConsentFor(type) ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Analytics Testing */}
          <div className="bg-blue-50 p-6 rounded-lg mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Analytics Testing</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{testData.pageViews}</div>
                <div className="text-sm text-gray-600">Page Views</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{testData.buttonClicks}</div>
                <div className="text-sm text-gray-600">Button Clicks</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{testData.formSubmissions}</div>
                <div className="text-sm text-gray-600">Form Submissions</div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleTestButtonClick}
                  disabled={!hasConsentFor(COOKIE_TYPES.ANALYTICS)}
                  className="btn-base btn-secondary disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Track Button Click
                </button>
                
                <button
                  onClick={handleTrackCustomEvent}
                  disabled={!hasConsentFor(COOKIE_TYPES.ANALYTICS)}
                  className="btn-base btn-secondary disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Track Custom Event
                </button>
              </div>

              <form onSubmit={handleTestFormSubmit} className="flex gap-3">
                <input
                  type="text"
                  placeholder="Test input for form tracking"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={!hasConsentFor(COOKIE_TYPES.ANALYTICS)}
                  className="btn-base btn-secondary disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Submit Form
                </button>
              </form>

              {!hasConsentFor(COOKIE_TYPES.ANALYTICS) && (
                <p className="text-sm text-orange-600 bg-orange-50 p-3 rounded">
                  Analytics tracking is disabled. Enable analytics cookies to test tracking functionality.
                </p>
              )}
            </div>
          </div>

          {/* Cookie Testing */}
          <div className="bg-green-50 p-6 rounded-lg mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookie Storage Testing</h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleStoreTestCookie}
                  disabled={!hasConsentFor(COOKIE_TYPES.FUNCTIONAL)}
                  className="btn-base btn-primary disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Store Test Cookie
                </button>
                
                <div className="text-sm text-gray-600">
                  Test Cookie Value: {testCookieValue || 'Not set'}
                </div>
              </div>

              {!hasConsentFor(COOKIE_TYPES.FUNCTIONAL) && (
                <p className="text-sm text-orange-600 bg-orange-50 p-3 rounded">
                  Functional cookies are disabled. Enable functional cookies to test cookie storage.
                </p>
              )}
            </div>
          </div>

          {/* Browser Cookie Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Browser Cookies</h2>
            
            <div className="text-sm">
              <p className="text-gray-600 mb-2">
                Terrapin-related cookies currently stored in your browser:
              </p>
              
              <div className="bg-white p-4 rounded border font-mono text-xs max-h-40 overflow-y-auto">
                {document.cookie
                  .split(';')
                  .filter(cookie => cookie.includes('terrapin') || cookie.includes('_ga'))
                  .map((cookie, index) => (
                    <div key={index} className="py-1">
                      {cookie.trim()}
                    </div>
                  ))
                }
                {!document.cookie.split(';').some(cookie => cookie.includes('terrapin') || cookie.includes('_ga')) && (
                  <div className="text-gray-500 italic">No Terrapin or Analytics cookies found</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CookieSettings 
        isOpen={showCookieSettings} 
        onClose={() => setShowCookieSettings(false)} 
      />
    </div>
  );
};

export default CookieTest; 